import { useEffect, useState } from "react";

import { ActionIcon, Box, Button, Flex, Stack, Textarea } from "@mantine/core";
import { IconCornerDownRight, IconPlus, IconX } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { ServiceConstants } from "constant";
import { BankSelectionService } from "services";
import { Bank, BankCommentResponse } from "types";

const { REQUEST_BANK_COMMENTS_LIST_ID, REQUEST_BANK_COMMENT_CREATE_ID, REQUEST_BANK_COMMENT_REPLY_ID } = ServiceConstants;

const TITLE_COLOR = "rgba(176, 70, 172, 1)";

interface BankCommentsSectionProps {
  bank: Bank;
  bankCreditRequestId: number;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const days = ["Nedelja", "Ponedeljak", "Utorak", "Sreda", "Četvrtak", "Petak", "Subota"];
  const dayName = days[date.getDay()];
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${dayName} ${hours}:${minutes}`;
};

export const BankCommentsSection = ({ bank, bankCreditRequestId }: BankCommentsSectionProps) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<BankCommentResponse[]>([]);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");

  const commentsResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_COMMENTS_LIST_ID + bank]
  );

  const replyResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_COMMENT_REPLY_ID]
  );

  const createResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_COMMENT_CREATE_ID]
  );

  useEffect(() => {
    if (bankCreditRequestId) {
      BankSelectionService.bankCommentsAll(bank, bankCreditRequestId);
    }
  }, [bank, bankCreditRequestId]);

  useEffect(() => {
    if (commentsResponse?.data) {
      const data = commentsResponse.data;
      const fetchedComments = Array.isArray(data) ? data : data.comments || [];
      setComments(fetchedComments);
    }
  }, [commentsResponse]);

  useEffect(() => {
    if (replyResponse?.data && bankCreditRequestId) {
      BankSelectionService.bankCommentsAll(bank, bankCreditRequestId);
      setReplyingTo(null);
      setReplyText("");
    }
  }, [replyResponse, bank, bankCreditRequestId]);

  useEffect(() => {
    if (createResponse?.data && bankCreditRequestId) {
      BankSelectionService.bankCommentsAll(bank, bankCreditRequestId);
      setIsAddingComment(false);
      setNewCommentText("");
    }
  }, [createResponse, bank, bankCreditRequestId]);

  const handleReply = (commentId: number) => {
    if (replyText.trim()) {
      BankSelectionService.bankCommentReply({
        bankCommentId: commentId,
        comment: replyText.trim(),
      });
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText("");
  };

  const handleCreateComment = () => {
    if (newCommentText.trim()) {
      BankSelectionService.bankCommentCreate({
        bank,
        bankCreditRequestId,
        comment: newCommentText.trim(),
      });
    }
  };

  const handleCancelCreate = () => {
    setIsAddingComment(false);
    setNewCommentText("");
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="md">
        <Text fw={600} c={TITLE_COLOR}>
          {t("bankSelection.comments.title")}
        </Text>
        {!isAddingComment && (
          <ActionIcon
            variant="filled"
            radius="xl"
            size="sm"
            color={TITLE_COLOR}
            onClick={() => setIsAddingComment(true)}
          >
            <IconPlus size={14} color="white" />
          </ActionIcon>
        )}
      </Flex>

      {isAddingComment && (
        <Box
          mb="md"
          p="sm"
          style={{
            backgroundColor: "white",
            borderRadius: 8,
            border: "1px solid rgba(50, 51, 67, 0.15)",
          }}
        >
          <Textarea
            placeholder={t("bankSelection.comments.placeholder")}
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            minRows={2}
            maxRows={4}
            autosize
            styles={{
              input: {
                border: "1px solid rgba(50, 51, 67, 0.15)",
                borderRadius: 8,
              },
            }}
          />
          <Flex justify="flex-end" mt="xs" gap="sm" align="center">
            <Text
              size="xs"
              c="gray"
              style={{ cursor: "pointer" }}
              onClick={handleCancelCreate}
            >
              {t("action.cancel")}
            </Text>
            <Button
              color="green"
              radius="md"
              size="xs"
              onClick={handleCreateComment}
              disabled={!newCommentText.trim()}
            >
              {t("bankSelection.comments.send")}
            </Button>
          </Flex>
        </Box>
      )}

      <Stack gap="md" style={{ maxHeight: 200, overflowY: "auto" }}>
        {comments.length > 0 ? (
          comments.map((comment) => (
            <Box key={comment.id}>
              <Flex justify="space-between" align="flex-start">
                <Box style={{ flex: 1 }}>
                  <Flex align="center" gap="xs" mb={4}>
                    <Text size="sm" fw={600}>
                      {comment.createdBy?.fullName || "Administrator"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDate(comment.createdAt)}
                    </Text>
                  </Flex>
                  <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                    {comment.comment}
                  </Text>

                  {comment.replies && comment.replies.length > 0 && (
                    <Stack gap="xs" mt="sm" ml="md">
                      {comment.replies.map((reply) => (
                        <Box
                          key={reply.id}
                          p="xs"
                          style={{
                            backgroundColor: "white",
                            borderRadius: 6,
                            borderLeft: "2px solid rgba(50, 51, 67, 0.15)",
                          }}
                        >
                          <Flex align="center" gap="xs" mb={2}>
                            <Text size="xs" fw={600}>
                              {reply.createdBy?.fullName || "Agent"}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatDate(reply.createdAt)}
                            </Text>
                          </Flex>
                          <Text size="xs" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                            {reply.comment}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => setReplyingTo(comment.id)}
                  title={t("bankSelection.comments.reply")}
                >
                  <IconCornerDownRight size={16} color="gray" />
                </ActionIcon>
              </Flex>

              {replyingTo === comment.id && (
                <Box
                  mt="sm"
                  p="sm"
                  style={{
                    backgroundColor: "white",
                    borderRadius: 8,
                    border: "1px solid rgba(50, 51, 67, 0.15)",
                  }}
                >
                  <Flex align="center" justify="space-between" mb="xs">
                    <Text size="xs" c="dimmed">
                      {`${t("bankSelection.comments.replyingTo")} ${comment.createdBy?.fullName || "Administrator"}`}
                    </Text>
                    <ActionIcon variant="subtle" size="xs" onClick={handleCancelReply}>
                      <IconX size={14} />
                    </ActionIcon>
                  </Flex>
                  <Textarea
                    placeholder={t("bankSelection.comments.replyPlaceholder")}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    minRows={2}
                    maxRows={4}
                    autosize
                    styles={{
                      input: {
                        border: "1px solid rgba(50, 51, 67, 0.15)",
                        borderRadius: 8,
                      },
                    }}
                  />
                  <Flex justify="flex-end" mt="xs">
                    <Button
                      color="green"
                      radius="md"
                      size="xs"
                      onClick={() => handleReply(comment.id)}
                      disabled={!replyText.trim()}
                    >
                      {t("bankSelection.comments.send")}
                    </Button>
                  </Flex>
                </Box>
              )}
            </Box>
          ))
        ) : (
          <Text size="sm" c="dimmed">
            {t("bankSelection.comments.empty")}
          </Text>
        )}
      </Stack>
    </Box>
  );
};
